import {
    BuildingType,
    EnergySupplies,
    EnergySupply,
    GreenLoanService,
    ProjectInformation
} from "../../services/GreenLoanService";
import {ChangeEvent, useContext, useEffect, useState} from "react";
import {GreenLoanContext} from "../App";
import {FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography} from "@mui/material";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {Event} from "@mui/icons-material";
import {energySupplyToNorwegian} from "../../utils/namingUtils";

interface Props {
    projectInformation: ProjectInformation,
    buildYearMax: number
}

export function ProjectInformationComponent(props: Props) {
    const {
        buildingType,
        buildYear,
        area,
        energySupply
    } = props.projectInformation
    const {buildYearMax} = props

    const {setProjectInformation, currentYear} = useContext(GreenLoanContext)

    const [areaInputErrorMsg, setAreaInputErrorMsg] = useState("")
    const [areaLocal, setAreaLocal] = useState(area)
    const [buildYearInputErrorMsg, setBuildYearInputErrorMsg] = useState("")
    const [buildYearDate, setBuildYearDate] = useState(new Date(buildYear.toString()))
    const buildYearMin = 1801

    function handleChange(changes: { buildYear?: number, buildingType?: BuildingType, area?: number, energySupply?: EnergySupply }) {
        setProjectInformation({...props.projectInformation, ...changes})
    }

    function handleAreaChange(value: number) {
        setAreaLocal(value)
        let minArea = 10;
        if (value >= minArea) {
            setAreaInputErrorMsg("")
            handleChange({area: value})
        } else {
            setAreaInputErrorMsg(`Areal må være større enn eller lik ${minArea}`)
        }
    }

    const additionalBuildYearHelperText = buildYearMax !== currentYear ? " Begrenses av oppgitt tidligere utført tiltak" : ""

    return (
        <div style={{marginBottom: "20px"}}>
            <Grid container
                  spacing={2}
                  direction="column"
            >
                <Grid item>
                    <Typography fontWeight="bold" color="primary" fontFamily="SpareBank1-medium">
                        Om boligen
                    </Typography>
                    <Typography>
                        Vi bruker informasjonen til å beregne energimerke og hvor mye du sparer.
                    </Typography>
                </Grid>
                <Grid item>
                    <FormControl style={{width: '100%'}}>
                        <InputLabel id="label-buildingType">Bygningstype</InputLabel>
                        <Select labelId="label-buildingType"
                                label="Bygningstype"
                                value={buildingType}
                                onChange={(e) =>
                                    handleChange({buildingType: e.target.value === BuildingType.ApartmentBlock.valueOf() ? BuildingType.ApartmentBlock : BuildingType.House})
                                }
                                fullWidth={true}
                        >
                            <MenuItem value={BuildingType.ApartmentBlock}>Leilighet</MenuItem>
                            <MenuItem value={BuildingType.House}>Småhus</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker views={["year"]}
                                    label="Byggeår"
                                    onChange={(value) => {
                                        if (value) {
                                            setBuildYearDate(value)
                                        }
                                    }}
                                    onAccept={(value) => {
                                        if (value) {
                                            handleChange({buildYear: value.getFullYear()})
                                        }
                                    }}
                                    onError={(reason, value) => {
                                        if (reason === null) {
                                            setBuildYearInputErrorMsg("")
                                            if (value) {
                                                handleChange({buildYear: new Date(value).getFullYear()})
                                            }
                                        }
                                        if (reason === "minDate") {
                                            setBuildYearInputErrorMsg(`Må være større enn ${(buildYearMin - 1)}.`)
                                        }
                                        if (reason === "maxDate") {
                                            setBuildYearInputErrorMsg(`Må være mindre enn eller lik ${buildYearMax}.`)
                                        }
                                    }}
                                    components={{
                                        OpenPickerIcon: (props) => (
                                            <Event {...props} color="primary"/>
                                        ),
                                    }}
                                    minDate={new Date(buildYearMin.toString())}
                                    maxDate={new Date(buildYearMax.toString())}
                                    value={buildYearDate}
                                    renderInput={(params) => <TextField error={buildYearInputErrorMsg !== ""}
                                                                        helperText={buildYearInputErrorMsg + additionalBuildYearHelperText}
                                                                        fullWidth={true} {...params} />}
                        />
                    </LocalizationProvider>

                </Grid>
                <Grid item>
                    <TextField type="number"
                               label="Bruksareal (m2)"
                               error={areaInputErrorMsg !== ''}
                               helperText={areaInputErrorMsg}
                               value={Number(areaLocal).toString()}
                               onChange={(e: ChangeEvent<HTMLInputElement>) => handleAreaChange(+e.target.value)}
                               fullWidth={true}
                    />
                </Grid>
                <Grid item>
                    <FormControl style={{width: '100%'}}>
                        <InputLabel id="label-buildingType">Energiforsyning</InputLabel>
                        <Select labelId="label-buildingType"
                                label="Energiforsyning"
                                value={energySupply}
                                onChange={(e) =>
                                    handleChange({energySupply: e.target.value as EnergySupply})
                                }
                                fullWidth={true}
                        >
                            {EnergySupplies.map(supply => {
                                return (
                                    <MenuItem key={supply} value={supply}>{energySupplyToNorwegian(supply)}</MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </div>
    )
}